import {createStyles} from '@mantine/core';
import React from 'react';

import {openSampleEntry, openSampleSequence} from 'actions/OnboardingActions';
import * as analytics from 'analytics/Analytics';
import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus} from 'llm/chat-library/ChatActions';
import {openDraftChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as NewUserFlowActions from 'onboarding/new-user-flow/NewUserFlowActions';
import InfoSidePanel from 'onboarding/new-user-flow/signup-flow-v2/InfoSidePanel';
import SignupScreen from 'onboarding/new-user-flow/signup-flow-v2/SignupScreen';
import SignupScreenCard from 'onboarding/new-user-flow/signup-flow-v2/SignupScreenCard';

interface ApplicationCardPanelProps {
  hoverOff: () => void;
  hoverNotebook: () => void;
  hoverMolBio: () => void;
}

const useStyles = createStyles((theme) => ({
  layout: {
    display: 'grid',
    gap: '56px',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 480px)',
    padding: '56px',
  },
  buttonPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
  },
}));

function ApplicationCardPanel(props: ApplicationCardPanelProps): JSX.Element {
  const notebookBodyText = 'Plan multi-day experiments, centralize data, and share experimental results.';
  const molBioBodyText =
    'Annotate sequences, perform alignments, and design plasmids, primers, and CRISPR guides.';
  const benchlingAIBodyText =
    'Ask Benchling AI to capture data, analyze results, and draft reports across your work.';
  const handleClickNotebook = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Notebook',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    openSampleEntry();
  };
  const handleClickMolBio = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Molecular Biology',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    openSampleSequence();
  };
  const showBenchlingAI = isAgentTypeEnabled(AgentType.ASSISTANT);
  const {classes} = useStyles();
  const handleClickBenchlingAI = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Benchling AI',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    // Open a draft chat rather than eagerly creating a chat session, matching the homepage AI
    // entry points, so the assistant opens ready for input without a spurious "Running now" task.
    openDraftChat({
      status: ChatDraftStatus.DRAFT,
      message: {text: '', selectedFiles: []},
      settings: {agentType: AgentType.ASSISTANT, publicDataEnabled: false},
    });
    SidepanelActions.openSection(NavConstants.SECTIONS.AI);
  };
  return (
    <div className={classes.buttonPanel}>
      <SignupScreenCard
        titleText="Notebook"
        bodyText={notebookBodyText}
        icon="book-open-cover"
        onClick={handleClickNotebook}
        buttonText="Show me an entry"
        hoverOn={props.hoverNotebook}
        hoverOff={props.hoverOff}
      />
      <SignupScreenCard
        titleText="Molecular Biology"
        bodyText={molBioBodyText}
        icon="dna"
        onClick={handleClickMolBio}
        buttonText="Open a sequence"
        hoverOn={props.hoverMolBio}
        hoverOff={props.hoverOff}
      />
      {showBenchlingAI && (
        <SignupScreenCard
          titleText="Benchling AI"
          bodyText={benchlingAIBodyText}
          icon="sparkles"
          iconVariant="inverse"
          onClick={handleClickBenchlingAI}
          buttonText="Open Chat"
          hoverOff={props.hoverOff}
        />
      )}
    </div>
  );
}

interface MolBioOrNotebookScreenProps {
  hoverOff: () => void;
  hoverNotebook: () => void;
  hoverMolBio: () => void;
}

export default function MolBioOrNotebookScreen(props: MolBioOrNotebookScreenProps): JSX.Element {
  const titleText = 'Choose what you want to see first';
  const bodyText =
    'Benchling offers a suite of capabilities for capturing, analyzing, and sharing your data.';
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <SignupScreen wide={true}>
      <div className={classes.layout}>
        <InfoSidePanel titleText={titleText} bodyText={bodyText} variant="redesigned" />
        <ApplicationCardPanel {...props} />
      </div>
    </SignupScreen>
  );
}
